import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { DatePicker } from "@circle-react/components/QuickPost/ModalForm/SubmitButton/DatePicker";
import { Icon } from "@circle-react-shared/Icon";
import { ToggleSwitch } from "@circle-react-shared/uikit/Form/ToggleSwitch";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { Typography } from "@circle-react-shared/uikit/Typography";

export const SettingsTab = () => {
  const toastr = useToast();
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const { watch, setValue } = useFormContext();

  const isLimitToAdminContent = watch("limit_to_admin_content") as boolean;
  const isFilterOldContent = watch("filter_old_content") as boolean;
  const isFilterHiddenPosts = watch("filter_hidden_posts") as boolean;
  const oldContentCutoffDate = watch("old_content_cutoff_date") as
    | string
    | null;

  const formatDateForDisplay = (dateString: string | null) => {
    if (!dateString) return null;

    const [year, month, day] = dateString.split("T")[0].split("-");
    const date = new Date(`${year}-${month}-${day}T12:00:00.000Z`);

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  const formattedDateValue = formatDateForDisplay(oldContentCutoffDate);

  const handleToggleChange = (field: string) => (checked: boolean) => {
    setValue(field, checked);

    if (field === "filter_old_content" && !checked) {
      setValue("old_content_cutoff_date", null);
    }
  };

  const onSaveDatePicker = (date: string) => {
    const dateOnly = date.split("T")[0];
    setValue("old_content_cutoff_date", dateOnly);
    setIsDatePickerVisible(false);
    toastr.success(
      t(
        "community_bot.bot_builder.knowledge_page.settings.date_set_successfully",
      ),
    );
  };

  const onCancelDatePicker = () => {
    setIsDatePickerVisible(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex w-full flex-row items-center justify-start">
        <Typography.LabelMd weight="semibold">
          {t("community_bot.bot_builder.knowledge_page.settings.title")}
        </Typography.LabelMd>
      </div>

      <div className="flex w-full flex-row items-start justify-start gap-4">
        <div className="flex grow flex-col gap-1">
          <Typography.LabelSm weight="medium">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.limit_to_admin_content.title",
            )}
          </Typography.LabelSm>
          <Typography.BodySm color="text-tertiary">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.limit_to_admin_content.description",
            )}
          </Typography.BodySm>
        </div>
        <ToggleSwitch
          name="limit_to_admin_content"
          checked={isLimitToAdminContent}
          onToggleChange={handleToggleChange("limit_to_admin_content")}
          variant="small"
        />
      </div>

      <div className="flex w-full flex-row items-start justify-start gap-4">
        <div className="flex grow flex-col gap-1">
          <Typography.LabelSm weight="medium">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.filter_hidden_posts.title",
            )}
          </Typography.LabelSm>
          <Typography.BodySm color="text-tertiary">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.filter_hidden_posts.description",
            )}
          </Typography.BodySm>
        </div>
        <ToggleSwitch
          name="filter_hidden_posts"
          checked={isFilterHiddenPosts}
          onToggleChange={handleToggleChange("filter_hidden_posts")}
          variant="small"
        />
      </div>

      <div className="flex w-full flex-row items-start justify-start gap-4">
        <div className="flex grow flex-col gap-1">
          <Typography.LabelSm weight="medium">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.filter_old_content.title",
            )}
          </Typography.LabelSm>
          <Typography.BodySm color="text-tertiary">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.filter_old_content.description",
            )}
          </Typography.BodySm>
        </div>
        <ToggleSwitch
          name="filter_old_content"
          checked={isFilterOldContent}
          onToggleChange={handleToggleChange("filter_old_content")}
          variant="small"
        />
      </div>

      {isFilterOldContent && (
        <div className="ml-0 flex flex-col gap-2">
          <Typography.LabelSm weight="medium">
            {t(
              "community_bot.bot_builder.knowledge_page.settings.select_date.label",
            )}
          </Typography.LabelSm>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsDatePickerVisible(true)}
              className="flex w-full items-center justify-between rounded-lg border border-[#e4e7eb] bg-white px-4 py-2 text-left text-base font-normal text-[#545861] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span>{formattedDateValue || t("select_a_date")}</span>
              <Icon type="16-calendar" size={16} className="!h-4 !w-4" />
            </button>
            <DatePicker
              datePickerVisible={isDatePickerVisible}
              enableTime={false}
              minDate={new Date("2000-01-01")}
              onCancel={onCancelDatePicker}
              value={
                oldContentCutoffDate
                  ? `${oldContentCutoffDate}T12:00:00.000Z`
                  : new Date().toISOString()
              }
              onSave={onSaveDatePicker}
              placement="bottom-end"
              // eslint-disable-next-line tailwindcss/no-custom-classname -- we dont any other option to change the gaps
              className="flatpickr-with-gaps"
              saveButtonVariant="circle"
            />
          </div>
        </div>
      )}
    </div>
  );
};
